import React, { useEffect } from "react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { isMobile } from "react-device-detect";
import styled from "styled-components";

import { REGISTER_TRADEMARK_CODE_ARRAY, SEARCH_TRADEMARK_CODE_ARRAY } from "_constants";

import { getProductsFiles, handleOrderNotes, parseRequiredActions } from "_helpers";

import { ordersActions } from "_store/actions";

import GeneralInfo from "components/OrderDetail/GeneralInfo";
import OrderDetailsMobile from "components/OrderDetail/OrderDetailsMobile";
import OrderDetailsDesktop from "components/OrderDetail/OrderDetailsDesktop";
import RequiredActions from "components/RequiredActions/RequiredActions";
import { ErrorModal } from "components/Modals/ErrorModal";

// import SpecimenAlert from "containers/SpecimenAlert";

import { SpinnerWrapper } from "widget/Spinner";
import ChargeUSPTOFeeAlert from "containers/ChargeUSPTOFeeAlert";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: 500px;

  animation: 0.2s linear fadeIn;
  @keyframes fadeIn {
    from {
      opacity: 0%;
    }
    to {
      opacity: 100%;
    }
  }
`;

function OrderDetails() {
  const dispatch = useDispatch();
  const { orderDetails, error, orderDetailsLoading } = useSelector(({ orders }) => orders);
  const { requiredActions } = useSelector(({ requiredActions }) => requiredActions);

  const { orderId } = useParams();

  useEffect(() => {
    dispatch(ordersActions.getOrder(orderId));
  }, [orderId, dispatch]);

  if (!orderDetails)
    return (
      <Container key="container-orderdetails-alert">
        {(orderDetailsLoading && <SpinnerWrapper />) || (error && <ErrorModal />)}
      </Container>
    );

  const productWithUSPTOFeeError = orderDetails?.products?.find((product) => {
    return product?.processingResult?.processingError === "USPTOFeeFailedIssue";
  });

  const registerDetails = orderDetails?.products.find(({ code: { code } }) =>
    REGISTER_TRADEMARK_CODE_ARRAY.includes(code)
  );
  const logoDesignServiceDetails = orderDetails?.products.find(({ code: { code } }) =>
    ["TRADEMARK_Logo_Design_Service"].includes(code)
  );
  const assessmentDetails = orderDetails?.products.find(({ code: { code } }) =>
    ["TRADEMARK_Legal_Team_Trademark_Assessment"].includes(code)
  );
  const hasSearch = !!orderDetails?.products.find(({ code: { code } }) => SEARCH_TRADEMARK_CODE_ARRAY.includes(code));
  const hasMonitoring = !!orderDetails?.products.find(
    ({ code: { code } }) => code === "TRADEMARK_Trademark_Monitoring"
  );
  const classes = registerDetails?.classes;
  const coOwners = registerDetails?.coOwners;
  const individual = registerDetails?.individual;
  const organization = registerDetails?.organization;
  const notes = handleOrderNotes(orderDetails?.notes);
  const productsFiles = logoDesignServiceDetails
    ? getProductsFiles(
        orderDetails?.products,
        [],
        [
          "TRADEMARK_LOGO_CONCEPT_1",
          "TRADEMARK_LOGO_CONCEPT_2",
          "TRADEMARK_LOGO_CONCEPT_3",
          "TRADEMARK_LOGO_CONCEPT_4",
          "TRADEMARK_LOGO_CONCEPT_5",
          "TRADEMARK_LOGO_CONCEPT_6",
          "TRADEMARK_LOGO_SAMPLE_MARKETING_PACKAGE",
          "TRADEMARK_LOGO_SOURCE",
        ],
        "BY_TYPE"
      )
    : getProductsFiles(orderDetails?.products, ["TRADEMARK_MAIL"], [], "BY_DATE");

  const assessments = {
    isProductExist: Boolean(assessmentDetails),
    isReady: assessmentDetails?.processingResult?.status === "Completed",
  };

  const actions = parseRequiredActions(requiredActions, orderDetails);

  const orderDetailsProps = {
    orderId,
    orderDetails,
    productsFiles,
    registerDetails,
    logoDesignServiceDetails,
    hasSearch,
    hasMonitoring,
    individual,
    organization,
    coOwners,
    classes,
    notes,
    assessments,
  };

  return (
    <Container key="container-orderdetails-main">
      {!!actions?.length && <RequiredActions actions={actions} />}
      <GeneralInfo orderDetails={orderDetails} />
      {!!orderDetails.products && productWithUSPTOFeeError && (
        <ChargeUSPTOFeeAlert orderId={orderId} product={productWithUSPTOFeeError} />
      )}

      {isMobile ? <OrderDetailsMobile {...orderDetailsProps} /> : <OrderDetailsDesktop {...orderDetailsProps} />}
    </Container>
  );
}

export default OrderDetails;
